import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { SeriesSummary } from '@contracts/series';
import { useJoinSeries } from '../api/queries';
import { Button, MenuButton, Modal, useNotices } from '../ui';
import { byLastMet, seriesWhen } from './when';

/**
 * Two series that are one (PRD 60).
 *
 * Google gives a recurring meeting a new id when it is edited "this and
 * following", and an organiser's Thursday variant of a daily is an event of
 * its own — each a series here, with no settings and no history of the other.
 * Vladimir, 2026-09-24, on the Thursday 🔴 Alpha Daily: *"can we join them in
 * one series?"*
 */

/** The same title, ignoring case, spaces and emoji — "🔴 Alpha Daily" is "Alpha daily". */
function sameName(a: string, b: string): boolean {
  const norm = (t: string) =>
    t
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  return norm(a) !== '' && norm(a) === norm(b);
}

/**
 * "Another series is called the same" — on the page of either. The one with
 * fewer meetings joins the other, so the history that is kept is the longer.
 */
export function Namesakes({
  here,
  list,
}: {
  here: SeriesSummary;
  list: SeriesSummary[];
}): ReactElement | null {
  const [joining, setJoining] = useState<{ from: SeriesSummary; into: SeriesSummary } | null>(null);
  const namesakes = list.filter(
    (s) => s.seriesKey !== here.seriesKey && sameName(s.title, here.title),
  );
  if (namesakes.length === 0) return null;
  const other = namesakes[0];
  const [from, into] = other.instances > here.instances ? [here, other] : [other, here];
  return (
    <div className="band">
      <div className="stack" style={{ gap: 4 }}>
        <span className="t-ui">
          Another series is also called “{other.title}” — {seriesWhen(other)}.
        </span>
        <span className="mono">
          Your calendar keeps them as two recurring events. Joined, they are one series: one
          history, one set of settings, one Slack channel.
        </span>
      </div>
      <Button size="sm" variant="primary" onClick={() => setJoining({ from, into })}>
        Join them
      </Button>
      {joining ? (
        <JoinModal from={joining.from} into={joining.into} onClose={() => setJoining(null)} />
      ) : null}
    </div>
  );
}

/** "Same series as…" — any other series this person is in, namesakes first. */
export function SameSeriesAs({
  here,
  list,
}: {
  here: SeriesSummary;
  list: SeriesSummary[];
}): ReactElement | null {
  const [into, setInto] = useState<SeriesSummary | null>(null);
  const others = list
    .filter((s) => s.seriesKey !== here.seriesKey)
    .sort(
      (a, b) =>
        Number(sameName(b.title, here.title)) - Number(sameName(a.title, here.title)) ||
        byLastMet(a, b),
    );
  if (others.length === 0) return null;
  return (
    <>
      <MenuButton
        label="Same series as…"
        size="sm"
        items={others.map((s) => ({
          id: s.seriesKey,
          label: s.title,
          sub: seriesWhen(s),
          onSelect: () => setInto(s),
        }))}
      />
      {into ? <JoinModal from={here} into={into} onClose={() => setInto(null)} /> : null}
    </>
  );
}

function JoinModal({
  from,
  into,
  onClose,
}: {
  from: SeriesSummary;
  into: SeriesSummary;
  onClose: () => void;
}): ReactElement {
  const join = useJoinSeries();
  const navigate = useNavigate();
  const { show } = useNotices();
  const go = () =>
    join.mutate(
      { from: from.seriesKey, into: into.seriesKey },
      {
        onSuccess: (done) => {
          onClose();
          show({
            tone: 'success',
            text: `One series now — ${done.meetings} ${done.meetings === 1 ? 'meeting' : 'meetings'} moved in, and the next ones will follow.`,
          });
          void navigate(`/series/${encodeURIComponent(done.seriesKey)}`);
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  return (
    <Modal
      title="Join them into one series?"
      onClose={onClose}
      foot={
        <>
          <Button variant="quiet" onClick={onClose}>
            Keep them apart
          </Button>
          <Button variant="primary" onClick={go} working={join.isPending ? 'Joining…' : false}>
            Join them
          </Button>
        </>
      }
    >
      <p className="t-body">
        “{from.title}” ({from.instances} {from.instances === 1 ? 'meeting' : 'meetings'}) joins “
        {into.title}” ({into.instances} {into.instances === 1 ? 'meeting' : 'meetings'}).
      </p>
      <p className="t-secondary">
        Its meetings, their actions and decisions, and the ones still on the calendar move over.
        Where both were set up differently, “{into.title}” keeps its own settings — its Slack
        channel, Asana project and language — and takes the other’s only where it had none. The app
        cannot split them again yet.
      </p>
    </Modal>
  );
}
